<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {CapacityFramework, Progression, TeamSummary} from "@/types";
import CapacityFrameworkLayout from "@/layouts/CapacityFrameworkLayout.vue";
import {useDate} from "@/composables/date";
import {ref, watch} from "vue";
import {useTeam} from "@/composables/team";
import {router} from "@inertiajs/vue3";

defineOptions({layout: [BaseLayout, CapacityFrameworkLayout]})

const {shortDate} = useDate()
const {teams} = useTeam()

interface Props {
    title: string
    capacityFramework: CapacityFramework
    category: 'overview'|'users'|'assessments'|'progression'|'public'|'versions'
    headers: Array<{
        id: number,
        start_date: string,
        title: string
        capacity_framework_version_id: number
    }>
    items: Progression
    team: TeamSummary
}

const props = defineProps<Props>();

const selectedTeam = ref<TeamSummary>(props.team);

watch(selectedTeam, (newTeam) => {
    router.reload({
        only: ['items', 'team', 'headers'],
        data: {
            team_id: newTeam.id
        }
    })
})
</script>

<template>
    <v-row
        class="mt-3 border-t-sm"
    >
        <v-col
            cols="12"
        >
            <v-select
                v-model="selectedTeam"
                :items="teams"
                width="350"
                return-object
                hide-selected
                hide-details
                item-title="name"
                item-value="id"
                density="compact"
                class="float-end"
                no-data-text="No resources found"
                :list-props="{
                    class: 'py-0'
                }"
                :menu-props="{
                    location: 'bottom'
                }"
            />
        </v-col>
    </v-row>
    <v-row class="mt-3">
        <v-col
            cols="12"
            class="py-0"
        >
            <v-table
                v-if="Object.keys(props.items).length > 0"
                class="bg-transparent"
            >
                <thead>
                    <tr class="text-medium-emphasis">
                        <th>
                            Category
                        </th>
                        <th>
                            Capacity indicator
                        </th>
                        <th
                            v-for="header in props.headers"
                            :key="header.id"
                            style="min-width: fit-content !important;max-width: 120px !important;"
                            class="text-pre"
                        >
                            <v-tooltip
                                location="top"
                                content-class="bg-black border-sm text-tooltip"
                                max-width="500"
                            >
                                <template #activator="{ props: bind }">
                                    <div
                                        v-bind="bind"
                                        class="cursor-help"
                                    >
                                        {{ shortDate(header.start_date) }}
                                    </div>
                                </template>
                                {{ header.title }} v{{ header.capacity_framework_version_id }}
                            </v-tooltip>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(assessments,key) in props.items"
                        :key="key"
                    >
                        <td style="width: 150px !important;">
                            {{ key.split('|')[0] }}
                        </td>
                        <td>
                            {{ key.split('|')[1] }}
                        </td>
                        <td
                            v-for="(header, assessment_id) in headers"
                            :key="`${key}-${assessment_id}`"
                            style="width: 100px !important;"
                            class="text-start"
                        >
                            <v-menu
                                v-if="assessments[assessment_id]"
                                location="top"
                                max-width="450"
                                :close-on-content-click="false"
                            >
                                <template #activator="{ props: bind }">
                                    <v-rating
                                        v-bind="bind"
                                        :model-value="(assessments[assessment_id].option_order + 1)"
                                        :length="assessments[assessment_id].options_count"
                                        class="cursor-pointer"
                                        empty-icon="mdi-circle-outline"
                                        full-icon="mdi-check-circle"
                                        density="compact"
                                        disabled
                                    />
                                </template>
                                <v-card
                                    variant="flat"
                                    rounded="lg"
                                    class="bg-white pa-0"
                                    elevation="0"
                                    border="sm"
                                >
                                    <v-card-text class="text-tooltip font-weight-medium pa-3 border-b-sm">
                                        {{ assessments[assessment_id].category_title }}: {{ assessments[assessment_id].capacity_indicator_title }}
                                    </v-card-text>
                                    <v-card-text class="text-tooltip font-weight-medium pa-3">
                                        Selected option: <br><span class="font-weight-regular">{{ assessments[assessment_id].option_value }}</span>
                                    </v-card-text>
                                    <v-card-text class="text-tooltip pa-3">
                                        Comment: <br><span class="font-weight-regular">{{ assessments[assessment_id].comment ?? 'None' }}</span>
                                    </v-card-text>
                                </v-card>
                            </v-menu>
                        </td>
                    </tr>
                </tbody>
            </v-table>
            <v-sheet
                v-else
                class="mt-15 pt-15"
            >
                <v-row
                    class="fill-height"
                    align="center"
                    justify="center"
                >
                    <v-col
                        cols="12"
                        class="text-center"
                    >
                        <v-avatar size="128">
                            <v-icon
                                size="128"
                                color="grey-lighten-2"
                            >
                                mdi-emoticon-sad-outline
                            </v-icon>
                        </v-avatar>
                        <h2 class="text-h5 font-weight-bold">
                            No enough assessments found.
                        </h2>
                        <p>
                            You have not completed enough assessments yet.
                        </p>
                    </v-col>
                </v-row>
            </v-sheet>
        </v-col>
    </v-row>
</template>

<style scoped>
.text-tooltip {
    font-size: 0.825rem;
}

:deep(th) {
    font-weight: bold !important;
    font-size: 0.875rem !important;
}
</style>
